import type React from "react";
import type { FC } from "react";
import { Box, Icon, Switch } from "senf-atomic-design-system";
import { cn } from "../../../util/classNameConstructor";
import type { IconName } from "../icon/names";
import TooltipProvider from "../tooltip/TooltipProvider";

interface LabelledSwitchProps {
  name: string;
  label: string;
  labelStyle?: string;
  subLabel?: string;
  subLabelStyle?: string;
  toggled: boolean;
  handleSetToggle: (toggled: boolean) => void;
  disabled?: boolean;
  disabledText?: string;
  alignSwitch?: "left" | "right";
  leadingIcon?: IconName;
  className?: string;
  prefix?: React.ReactNode;
}

const LabelledSwitch: FC<LabelledSwitchProps> = ({
  name,
  label,
  disabled,
  disabledText,
  labelStyle,
  subLabel,
  subLabelStyle,
  toggled,
  handleSetToggle,
  leadingIcon,
  alignSwitch = "right",
  className,
  prefix,
}) => {
  const SwitchComp = (
    <Switch
      id={name}
      checked={toggled}
      disabled={disabled}
      onCheckedChange={() => handleSetToggle(!toggled)}
      className={cn("shrink-0", !disabled && "pointer-events-auto")}
    />
  );

  return (
    <TooltipProvider
      primaryContent={disabledText}
      // onClick="openTooltip"
      className="pointer-events-auto flex items-center"
      critical={!!disabled}
      disabled={disabled}
      noTooltip={!disabled}
    >
      <label
        htmlFor={name}
        className={cn(
          "flex min-h-8 w-full items-center gap-2.5 pointer-events-none",
          className,
        )}
      >
        {alignSwitch === "left" && SwitchComp}

        {prefix}

        {leadingIcon && <Icon name={leadingIcon} className="shrink-0" />}
        <div className={"flex w-full items-center justify-between gap-5"}>
          <div className={cn("flex flex-col", disabled && "opacity-50")}>
            <div className={cn(labelStyle || "body-md", "pointer-events-auto")}>
              {label}
            </div>
            {subLabel && (
              <p
                className={cn(
                  subLabelStyle || "body-md text-text-subdued",
                  "pointer-events-auto",
                )}
              >
                {subLabel}
              </p>
            )}
          </div>
          {alignSwitch === "right" && SwitchComp}
        </div>
      </label>
    </TooltipProvider>
  );
};

export default LabelledSwitch;
